/**
 * BookRow: a titled, horizontally scrolling shelf of BookCards.
 * (cockatrice-ui `src/ui/BookRow.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/book-row.css';
import { createUniqueId, mergeProps, splitProps } from 'solid-js';
import { el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface BookRowProps extends Attributes {
  /** Section heading (h2), also the accessible name. */
  readonly title?: string | undefined;
  /** Count shown after the title. */
  readonly count?: number | undefined;
  /** Link or button on the right of the heading. */
  readonly action?: Child;
  /** Accessible name when there is no title. */
  readonly label?: string | undefined;
  /** BookCards and a NewBookTile. */
  readonly children?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function BookRow(props: BookRowProps): Child {
  const [local, rest] = splitProps(props, ['title', 'count', 'action', 'label', 'children', 'class']);
  const id = createUniqueId();
  const titled = (): boolean => local.title !== undefined && local.title !== '';
  return el(
    'section',
    mergeProps({
      class: () => `ui-book-row${local.class !== undefined && local.class !== '' ? ` ${local.class}` : ''}`,
      'aria-labelledby': () => (titled() ? id : undefined),
      'aria-label': () => (titled() ? undefined : local.label),
    }, rest),
    when(
      () => titled() || Boolean(local.action),
      () =>
        el(
          'header',
          { class: 'ui-book-row__head' },
          when(titled, () =>
            el(
              'h2',
              { class: 'ui-book-row__title', id },
              () => local.title,
              when(
                () => local.count !== undefined,
                () => el('span', { class: 'ui-book-row__count' }, () => local.count),
              ),
            ),
          ),
          when(
            () => Boolean(local.action),
            () => el('div', { class: 'ui-book-row__action' }, () => local.action),
          ),
        ),
    ),
    el('div', { class: 'ui-book-row__scroller' }, () => local.children),
  );
}
